import { stepMonth } from './MonthStepper.js'
import type { HealthAction, HealthFinding, HealthLens, HealthSeverity, MonthHealthView, UnitHealth } from '../types.js'

/** The pure parts of the Month card (month health plan §3): the lens tiles, the ordering, the wording. */

export const LENS_ORDER: readonly HealthLens[] = ['census', 'workers', 'units']
export const LENS_LABEL: Record<HealthLens, string> = { census: 'Census', workers: 'Workers', units: 'Units' }
const SEVERITY_RANK: Record<HealthSeverity, number> = { block: 0, warn: 1, info: 2 }
export const SEVERITY_LABEL: Record<HealthSeverity, string> = { block: 'Holds the release', warn: 'Warning', info: 'Information' }
/** The mark beside a finding: coral for a block, gold for a warning, marine for information. */
export const SEVERITY_DOT: Record<HealthSeverity, string> = { block: 'bg-coral', warn: 'bg-gold', info: 'bg-marine/40' }
export const VERDICT_LABEL: Record<MonthHealthView['verdict'], string> = { ready: 'Ready', warnings: 'Warnings', blocked: 'Not ready' }
export const VERDICT_CLASS: Record<MonthHealthView['verdict'], string> = { ready: 'bg-green/15 text-green border-green/50', warnings: 'bg-gold/30 text-ink border-gold', blocked: 'bg-coral/10 text-coral border-coral/40' }

/** What each button says. */
export const ACTION_LABEL: Record<HealthAction, string> = {
  record_census: 'Record the census',
  configure_units: 'Set up the units',
  set_staffing_plan: 'Set the staffing plan',
  publish_shifts: 'Publish shifts',
  add_worker: 'Add a worker',
  edit_worker: 'Open their row',
  remind_all: 'Remind all',
  build_month: 'Build the month',
  assign_seats: 'Assign the seats',
  open_board: 'Open the board',
  release: 'Release to workers',
}

export interface LensTile {
  lens: HealthLens
  label: string
  /** Findings that are not information. */
  count: number
  worst: HealthSeverity | null
  /** The first finding's message, the tile's sub-line. */
  headline: string
}

/** One tile per lens: how many things need doing there, the worst of them, and the first of them in words. */
export function lensTiles(view: Pick<MonthHealthView, 'findings'>): LensTile[] {
  return LENS_ORDER.map((lens) => {
    const findings = sortFindings(view.findings.filter((entry) => entry.lens === lens))
    const acting = findings.filter((entry) => entry.severity !== 'info')
    return { lens, label: LENS_LABEL[lens], count: acting.length, worst: findings[0]?.severity ?? null, headline: acting[0]?.message ?? (findings[0]?.message ?? 'Nothing to do') }
  })
}

/** Inside a severity, the month's own steps come first (build, release), then the seats, then what is published, then the roster, then the configuration. */
const KEY_RANK: Record<HealthFinding['key'], number> = {
  not_built: 0,
  run_stale: 1,
  not_released: 2,
  unfilled_over_cap: 3,
  fill_below_minimum: 3,
  // A seat never published comes before an open one of the same kind: publishing is the earlier step.
  minimum_not_published: 4,
  below_minimum_line: 4.1,
  requirement_not_published: 5,
  below_requirement: 5.1,
  nothing_published: 6,
  days_without_shifts: 7,
  unit_without_shifts: 8,
  shifts_on_unknown_unit: 9,
  preferences_missing: 10,
  preferences_closed_missing: 11,
  role_short_on_roster: 12,
  credentials_lapsing: 13,
  census_missing: 14,
  unit_without_capacity: 15,
  unit_without_census: 16,
  no_units: 17,
  no_staffing_plan: 18,
  over_published: 19,
  no_prn_or_agency: 20,
  census_expected_only: 21,
}

/** Blocks first, then warnings, then information; inside a severity by the step order above, then the API's order (unit, shift type, role). */
export function sortFindings(findings: readonly HealthFinding[]): HealthFinding[] {
  return [...findings].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || KEY_RANK[a.key] - KEY_RANK[b.key])
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "November 2026". */
export function monthLabel(period: string): string {
  return new Date(`${period}-01T00:00:00Z`).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "Nov 21–30", "Nov 2, 5, 9", "Nov 2, 5, 9 and 4 more": a run of consecutive days as one span, otherwise the first few. */
export function describeDates(dates: readonly string[], shown = 3): string {
  if (dates.length === 0) {
    return ''
  }
  const sorted = [...dates].sort()
  const first = sorted[0] as string
  const month = MONTH_SHORT[Number(first.slice(5, 7)) - 1] ?? first.slice(0, 7)
  const day = (date: string): number => Number(date.slice(8, 10))
  const consecutive = sorted.every((date, index) => index === 0 || day(date) === day(sorted[index - 1] as string) + 1)
  if (consecutive && sorted.length > 1) {
    return `${month} ${day(first)}–${day(sorted[sorted.length - 1] as string)}`
  }
  const head = sorted.slice(0, shown).map(day).join(', ')
  const rest = sorted.length - shown
  return rest > 0 ? `${month} ${head} and ${rest} more` : `${month} ${head}`
}

/**
 * The months the arrows step through: back to the facility's first month with a run or a published shift (the
 * period view's `firstPeriod`, demo history plan H0), and forward to the second month after this one, the furthest a
 * facility publishes and builds for. Never later than this month at the start, so the current month is always in reach.
 */
export function monthRange(today: Date, firstPeriod: string | null = null): { first: string; last: string } {
  const thisMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)).toISOString().slice(0, 7)
  return { first: firstPeriod !== null && firstPeriod < thisMonth ? firstPeriod : thisMonth, last: stepMonth(thisMonth, 2) }
}

/** Whether a month (`YYYY-MM`) ended before the one `today` is in: its days are worked, nothing in it is still to decide. */
export function isPastMonth(period: string, today: Date): boolean {
  return period < new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)).toISOString().slice(0, 7)
}

const dayLabel = (date: string): string => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/**
 * What the month's required seats were worked out from (26 October 2026: "899 seats of 868 required" read as off - the
 * seats were published ahead for the planned census, the requirement follows the census as counted, then forecast).
 * "actual to Oct 26, forecast after" where some days are counted; null for an API that does not say.
 */
export function requiredBasisText(units: readonly UnitHealth[]): string | null {
  const reported = units.filter((unit) => unit.daysActual !== undefined)
  if (reported.length === 0) {
    return null
  }
  const ahead = reported.reduce((sum, unit) => sum + (unit.daysExpected ?? 0) + unit.daysForecast, 0)
  const through = reported.map((unit) => unit.actualThrough ?? null)
  if (through.every((date) => date === null)) {
    return ahead > 0 ? 'all forecast' : null
  }
  if (ahead === 0) {
    return 'all actual'
  }
  // Every unit is counted at least this far; a unit counted further is still read as forecast after it.
  const shared = through.filter((date): date is string => date !== null).sort()[0]!
  return `actual to ${dayLabel(shared)}, forecast after`
}
